import { useCallback, useEffect, useState } from "react";
import { ConnectionBrandIcon } from "@/components/ConnectionBrandIcon";
import {
  SLACK_COMPANY_APP_MANIFEST,
  addSlackCompanyApp,
  fetchSlackCompanyApps,
  openSlackSignIn,
  removeSlackCompanyApp,
  slackWorkspaceConnectLink,
  slackWorkspaceName,
  startSlackWorkspaceConnect,
  type SlackCompanyApp,
} from "@/lib/slackCompanyApp";

async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

/** Inside the Slack card: connect through a workspace's own ATW Hub app. */
export function SlackWorkspaceConnect({
  initialWorkspace,
  onNotice,
}: {
  initialWorkspace?: string;
  onNotice: (message: string) => void;
}) {
  const [open, setOpen] = useState(Boolean(initialWorkspace));
  const [workspace, setWorkspace] = useState(initialWorkspace || "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function connect(event: React.FormEvent) {
    event.preventDefault();
    if (!slackWorkspaceName(workspace)) {
      setError("Enter your workspace address, like importgenius.slack.com.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await openSlackSignIn(async () => (await startSlackWorkspaceConnect(workspace)).authorizationUrl);
      onNotice(`Opened Slack for ${slackWorkspaceName(workspace)}.slack.com. Click Allow there, then come back here.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not start Slack sign-in.");
    } finally {
      setBusy(false);
    }
  }

  if (!open) {
    return (
      <button type="button" className="link-btn slack-company-toggle" onClick={() => setOpen(true)}>
        Does your company have its own ATW Hub Slack app? Connect through it
      </button>
    );
  }
  return (
    <form className="slack-company-connect" onSubmit={(e) => void connect(e)}>
      <label htmlFor="slack-company-workspace">Your company&apos;s Slack workspace</label>
      <div className="slack-company-inline">
        <input
          id="slack-company-workspace"
          className="settings-input"
          placeholder="importgenius.slack.com"
          autoComplete="off"
          value={workspace}
          onChange={(e) => setWorkspace(e.target.value)}
        />
        <button type="submit" className="primary-btn" disabled={busy}>
          {busy ? "Opening…" : "Connect Workspace"}
        </button>
      </div>
      {error ? (
        <p className="banner-error" role="alert">
          {error}
        </p>
      ) : (
        <p className="muted tight">Slack checks that you belong to the workspace. Your Hub email doesn&apos;t matter.</p>
      )}
    </form>
  );
}

/** For a Slack admin: add the workspace's own ATW Hub app so everyone there gets full Chat. */
export function SlackCompanyAppCard({ onNotice }: { onNotice: (message: string) => void }) {
  const [apps, setApps] = useState<SlackCompanyApp[]>([]);
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState("");
  const [confirmRemove, setConfirmRemove] = useState("");
  const [showForm, setShowForm] = useState(false);

  const refresh = useCallback(async () => {
    try {
      setApps(await fetchSlackCompanyApps());
    } catch {
      /* keep what's on screen */
    }
  }, []);

  useEffect(() => {
    void refresh();
    // Back from Slack's sign-in tab: the app may have just become active.
    window.addEventListener("focus", refresh);
    return () => window.removeEventListener("focus", refresh);
  }, [refresh]);

  async function copy(id: string, text: string) {
    if (await copyText(text)) {
      setCopied(id);
      window.setTimeout(() => setCopied(""), 1600);
    } else {
      onNotice("Your browser blocked copying. Select the text and copy it by hand.");
    }
  }

  async function save(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      await openSlackSignIn(async () => {
        const result = await addSlackCompanyApp(clientId, clientSecret);
        return result.authorizationUrl;
      });
      setClientSecret("");
      setShowForm(false);
      onNotice("Keys saved. Sign in to Slack in the new tab and click Allow to finish.");
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not save the app.");
    } finally {
      setBusy(false);
    }
  }

  async function remove(app: SlackCompanyApp) {
    setBusy(true);
    try {
      await removeSlackCompanyApp(app.id);
      setConfirmRemove("");
      onNotice(`${app.teamName || "The company Slack app"} was removed from Hub. People connected through it stay connected until they reconnect.`);
      await refresh();
    } catch (err) {
      onNotice(err instanceof Error ? err.message : "Could not remove the app.");
    } finally {
      setBusy(false);
    }
  }

  const formOpen = showForm || apps.length === 0;

  return (
    <section className="settings-card" id="slack-company-app">
      <div className="connector-row">
        <div className="connection-title">
          <ConnectionBrandIcon id="slack" label="Slack" />
          <div>
            <h2>Company Slack App</h2>
            <p className="muted tight">
              For Slack admins. Create ATW Hub as your workspace&apos;s own app so everyone there gets message
              previews and unread counts.
            </p>
          </div>
        </div>
        <span className="pill">{apps.length ? `${apps.length} added` : "Admin"}</span>
      </div>

      {apps.length ? (
        <ul className="simple-list">
          {apps.map((app) => (
            <li key={app.id} className="connector-row slack-company-app">
              <div>
                <strong>{app.teamName || "Waiting for first sign-in"}</strong>
                <p className="muted tight">
                  {app.status === "active"
                    ? `${app.teamDomain}.slack.com · ${app.connected} ${app.connected === 1 ? "person" : "people"} connected`
                    : `Client ID ${app.clientId} · sign in once to finish`}
                </p>
              </div>
              <div className="settings-btn-row">
                {app.status === "active" ? (
                  <button
                    type="button"
                    className="secondary-btn"
                    onClick={() => void copy(app.id, slackWorkspaceConnectLink(app.teamDomain))}
                  >
                    {copied === app.id ? "Copied" : "Copy Connect Link"}
                  </button>
                ) : null}
                {confirmRemove === app.id ? (
                  <>
                    <button type="button" className="secondary-btn danger-btn" disabled={busy} onClick={() => void remove(app)}>
                      Remove
                    </button>
                    <button type="button" className="secondary-btn" onClick={() => setConfirmRemove("")}>
                      Keep
                    </button>
                  </>
                ) : (
                  <button type="button" className="secondary-btn" onClick={() => setConfirmRemove(app.id)}>
                    Remove
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      ) : null}

      {formOpen ? (
        <>
          <ol className="settings-steps">
            <li>
              Sign in to your workspace at <code>api.slack.com/apps</code> and click <b>Create New App</b> →{" "}
              <b>From a manifest</b>, then pick your workspace.
            </li>
            <li>
              On the <b>YAML</b> tab, paste the app settings below and click <b>Create</b>.{" "}
              <button
                type="button"
                className="link-btn"
                onClick={() => void copy("manifest", SLACK_COMPANY_APP_MANIFEST)}
              >
                {copied === "manifest" ? "Copied" : "Copy app settings"}
              </button>
            </li>
            <li>
              Open <b>Install App</b> → <b>Install to Workspace</b> → <b>Allow</b>.
            </li>
            <li>
              In <b>Basic Information → App Credentials</b>, copy the Client ID and Client Secret into the boxes
              below.
            </li>
          </ol>
          <form className="slack-company-form" onSubmit={(e) => void save(e)}>
            <label htmlFor="slack-company-client-id">Client ID</label>
            <input
              id="slack-company-client-id"
              className="settings-input"
              inputMode="decimal"
              autoComplete="off"
              placeholder="1234567890.0987654321"
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
            />
            <label htmlFor="slack-company-client-secret">Client Secret</label>
            <input
              id="slack-company-client-secret"
              className="settings-input"
              type="password"
              autoComplete="off"
              value={clientSecret}
              onChange={(e) => setClientSecret(e.target.value)}
            />
            {error ? (
              <p className="banner-error" role="alert">
                {error}
              </p>
            ) : null}
            <div className="settings-btn-row">
              <button type="submit" className="primary-btn" disabled={busy || !clientId.trim() || !clientSecret.trim()}>
                {busy ? "Saving…" : "Save and Sign In to Slack"}
              </button>
              {apps.length ? (
                <button type="button" className="secondary-btn" onClick={() => setShowForm(false)}>
                  Cancel
                </button>
              ) : null}
            </div>
            <p className="muted tight">
              Hub encrypts the Client Secret and never shows it again. Signing in once checks the keys and tells Hub
              which workspace the app belongs to.
            </p>
          </form>
        </>
      ) : (
        <div className="settings-btn-row">
          <button type="button" className="secondary-btn" onClick={() => setShowForm(true)}>
            Add Another Workspace
          </button>
        </div>
      )}
    </section>
  );
}
